import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { SparklesIcon, FileTextIcon, BookmarkIcon, MessageSquareIcon, BriefcaseIcon, UserIcon, LifeBuoyIcon, XIcon, ChevronRightIcon, ChevronDownIcon, CheckIcon, LockIcon, LogInIcon } from 'lucide-react';
import { Logo } from './Logo';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
type Market = {
  code: string;
  name: string;
  flag: string;
  available: boolean;
};
// Use real flag images (flagcdn) — flag emoji don't render on all platforms
// (e.g. Windows shows the country code letters instead of the flag).
const flagUrl = (code: string) => `https://flagcdn.com/w80/${code.toLowerCase()}.png`;
const MARKETS: Market[] = [{
  code: 'AE',
  name: 'United Arab Emirates',
  flag: flagUrl('AE'),
  available: true
}, {
  code: 'SA',
  name: 'Saudi Arabia',
  flag: flagUrl('SA'),
  available: false
}, {
  code: 'QA',
  name: 'Qatar',
  flag: flagUrl('QA'),
  available: false
}, {
  code: 'KW',
  name: 'Kuwait',
  flag: flagUrl('KW'),
  available: false
}, {
  code: 'OM',
  name: 'Oman',
  flag: flagUrl('OM'),
  available: false
}, {
  code: 'BH',
  name: 'Bahrain',
  flag: flagUrl('BH'),
  available: false
}, {
  code: 'EG',
  name: 'Egypt',
  flag: flagUrl('EG'),
  available: false
}];
interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}
type Item = {
  to: string;
  label: string;
  icon: React.ElementType;
  state?: Record<string, unknown>;
};
type Section = {
  title: string;
  items: Item[];
};
export function MobileMenu({
  open,
  onClose
}: MobileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    hub,
    activateHub
  } = useBusiness();
  const {
    isAuthenticated,
    requireAuth,
    openLogin
  } = useAuth();
  const [market, setMarket] = useState<Market>(MARKETS[0]);
  const [regionSheetOpen, setRegionSheetOpen] = useState(false);
  // When logged out, only Discover is available from the menu. Everything else
  // is locked behind sign-in + phone verification.
  const isLocked = (to: string) => !isAuthenticated && to !== '/';
  const sections: Section[] = [{
    title: 'Browse',
    items: [{
      to: '/',
      label: 'Discover',
      icon: SparklesIcon,
      state: {
        reset: Date.now()
      }
    }, {
      to: '/requests',
      label: 'Requests',
      icon: FileTextIcon
    }, {
      to: '/saved',
      label: 'Saved',
      icon: BookmarkIcon
    }, {
      to: '/messages',
      label: 'Messages',
      icon: MessageSquareIcon
    }]
  },
  // Business modules appear only once a Business Hub is active — mirroring
  // the web portal's adaptive behavior.
  ...(hub?.active ? [{
    title: 'Business',
    items: [{
      to: '/business',
      label: 'Business Hub',
      icon: BriefcaseIcon
    }]
  } as Section] : []), {
    title: 'Account',
    items: [{
      to: '/profile',
      label: 'Profile',
      icon: UserIcon
    }, {
      to: '/help',
      label: 'Help & Support',
      icon: LifeBuoyIcon
    }]
  }];
  const navigateTo = (item: Item) => {
    navigate(item.to, item.state ? {
      state: item.state
    } : undefined);
    onClose();
  };
  const go = (item: Item) => {
    if (isLocked(item.to)) {
      onClose();
      requireAuth(() => navigateTo(item));
      return;
    }
    navigateTo(item);
  };
  const isActive = (to: string) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
  // Activate the Business Hub immediately — this flips the hub to active+new,
  // which surfaces the global welcome modal so the user can start right away
  // (no detour through the Discover chat).
  const activateBusinessHub = () => {
    if (!isAuthenticated) {
      onClose();
      requireAuth(() => activateHub('product'));
      return;
    }
    activateHub('product');
    onClose();
  };
  const openBusinessHub = () => {
    navigate('/business');
    onClose();
  };
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-1963">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-[120] flex" data-id="element-1964">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-1965" />

          {/* Drawer */}
          <motion.aside initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 320
      }} className="relative w-[82%] max-w-[320px] h-full bg-white flex flex-col shadow-2xl" role="dialog" aria-modal="true" aria-label="Menu" data-id="element-1966">
            {/* Header */}
            <div className="pt-12 px-5 pb-4 flex items-center justify-between border-b border-slate-100" data-id="element-1967">
              <Logo size="sm" data-id="element-1968" />
              <button onClick={onClose} aria-label="Close menu" className="p-2 -mr-1 rounded-full hover:bg-slate-100 transition-colors" data-id="element-1969">
                <XIcon className="w-5 h-5 text-slate-500" data-id="element-1970" />
              </button>
            </div>

            {/* Profile mini-card / Sign-in CTA */}
            {isAuthenticated ? <>
                <button onClick={() => go({
            to: '/profile',
            label: 'Profile',
            icon: UserIcon
          })} className="mx-3 mt-3 flex items-center gap-3 px-3 py-3 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors text-left" data-id="element-1971">
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=128&h=128&q=80" alt="Your profile" className="w-11 h-11 rounded-full object-cover border border-white shadow-sm" data-id="element-1972" />
                  <div className="min-w-0 flex-1" data-id="element-1973">
                    <p className="text-[14px] font-semibold text-dark-navy truncate" data-id="element-1974">
                      Your account
                    </p>
                    <p className="text-[12px] text-slate-500 truncate" data-id="element-1975">
                      View profile & settings
                    </p>
                  </div>
                  <ChevronRightIcon className="w-4 h-4 text-slate-400" data-id="element-1976" />
                </button>

                {/* Market / region selector */}
                <button onClick={() => setRegionSheetOpen(true)} className="mx-3 mt-2 flex items-center gap-2.5 px-3 py-2.5 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-left" data-id="element-1977">
                  <img src={market.flag} alt="" className="w-6 h-[18px] rounded-[3px] object-cover flex-shrink-0 ring-1 ring-slate-200" data-id="element-1978" />
                  <span className="min-w-0 flex-1" data-id="element-1979">
                    <span className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5" data-id="element-1980">
                      Market
                    </span>
                    <span className="block text-[13px] font-semibold text-dark-navy truncate leading-tight" data-id="element-1981">
                      {market.name}
                    </span>
                  </span>
                  <ChevronDownIcon className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-1982" />
                </button>
              </> : <button onClick={() => {
          onClose();
          openLogin();
        }} className="mx-3 mt-3 flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-dark-navy text-white text-left active:scale-[0.98] transition-transform shadow-sm" data-id="element-1983">
                <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-1984">
                  <LogInIcon className="w-5 h-5 text-white" data-id="element-1985" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-1986">
                  <p className="text-[14px] font-bold truncate" data-id="element-1987">
                    Sign in to Talab
                  </p>
                  <p className="text-[12px] text-white/60 truncate" data-id="element-1988">
                    Verify your phone to unlock everything
                  </p>
                </div>
                <ChevronRightIcon className="w-4 h-4 text-white/60" data-id="element-1989" />
              </button>}

            {/* Sections */}
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-id="element-1990">
              {sections.map(section => <div key={section.title} data-id="element-1991">
                  <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-1992">
                    {section.title}
                  </p>
                  <div className="space-y-0.5" data-id="element-1993">
                    {section.items.map(item => {
                const Icon = item.icon;
                const active = isActive(item.to);
                const locked = isLocked(item.to);
                return <button key={item.label} onClick={() => go(item)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${active ? 'bg-primary-indigo/5 text-primary-indigo' : locked ? 'text-slate-400 hover:bg-slate-50' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-1994">
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${active ? 'bg-primary-indigo/10' : 'bg-slate-100'}`} data-id="element-1995">
                            <Icon className={`w-[18px] h-[18px] ${active ? 'text-primary-indigo' : locked ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-1996" />
                          </span>
                          <span className="flex-1 text-left text-[14px] font-medium" data-id="element-1997">
                            {item.label}
                          </span>
                          {locked && <LockIcon className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" data-id="element-1998" />}
                        </button>;
              })}
                  </div>
                </div>)}
            </nav>

            {/* Business Hub activation CTA — only before the hub is active.
                Once active, the "Business Hub" nav item above is the single
                entry point (no duplicate footer button). */}
            {!hub?.active && <div className="px-4 pt-2 pb-7 border-t border-slate-100" data-id="element-1999">
                <button onClick={activateBusinessHub} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-primary-indigo to-ai-purple text-white text-[14px] font-semibold shadow-sm active:scale-[0.98] transition-transform" data-id="element-2000">
                  <BriefcaseIcon className="w-4 h-4" data-id="element-2001" />
                  Activate Business Hub
                </button>
                <p className="mt-2 text-center text-[11px] text-slate-400 leading-snug" data-id="element-2002">
                  Sell products, manage listings & leads with an AI assistant
                </p>
              </div>}

            {/* Region / market selector sheet */}
            <AnimatePresence data-id="element-2003">
              {regionSheetOpen && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 z-[10] flex flex-col justify-end" data-id="element-2004">
                  <div className="absolute inset-0 bg-dark-navy/30 backdrop-blur-[2px]" onClick={() => setRegionSheetOpen(false)} data-id="element-2005" />
                  <motion.div initial={{
              y: '100%'
            }} animate={{
              y: 0
            }} exit={{
              y: '100%'
            }} transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300
            }} className="relative bg-white rounded-t-3xl pb-7 pt-3 max-h-[75%] flex flex-col" data-id="element-2006">
                    <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-3" data-id="element-2007" />
                    <h3 className="px-5 text-[15px] font-bold text-dark-navy mb-1" data-id="element-2008">
                      Choose your market
                    </h3>
                    <p className="px-5 text-[12px] text-slate-400 mb-3" data-id="element-2009">
                      Talab is live in the UAE. More markets are coming soon.
                    </p>
                    <div className="overflow-y-auto px-2.5" data-id="element-2010">
                      {MARKETS.map(m => {
                  const active = m.code === market.code;
                  return <button key={m.code} disabled={!m.available} onClick={() => {
                    if (!m.available) return;
                    setMarket(m);
                    setRegionSheetOpen(false);
                  }} className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl transition-colors ${active ? 'bg-primary-indigo/5' : m.available ? 'active:bg-slate-50' : 'opacity-60'}`} data-id="element-2011">
                            <img src={m.flag} alt="" className="w-7 h-5 rounded-[3px] object-cover flex-shrink-0 ring-1 ring-slate-200" data-id="element-2012" />
                            <span className="flex-1 text-left text-[14px] font-semibold text-dark-navy truncate" data-id="element-2013">
                              {m.name}
                            </span>
                            {active ? <CheckIcon className="w-5 h-5 text-primary-indigo flex-shrink-0" data-id="element-2014" /> : !m.available ? <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded-lg" data-id="element-2015">
                                Soon
                              </span> : null}
                          </button>;
                })}
                    </div>
                  </motion.div>
                </motion.div>}
            </AnimatePresence>
          </motion.aside>
        </motion.div>}
    </AnimatePresence>, mountNode);
}